/** Itens de valor do sinistro (material, mão de obra, etc.) */

export const ITEM_VALOR_TIPO = "item_valor";

export type SinistroItemValor = {
  id: string;
  descricao: string;
  valor: number;
};

export function isItemValorAnexo(item: string): boolean {
  try {
    const parsed = JSON.parse(item) as { _tipo?: string };
    return parsed?._tipo === ITEM_VALOR_TIPO;
  } catch {
    return false;
  }
}

export function itemValorToAnexoString(item: SinistroItemValor): string {
  return JSON.stringify({ _tipo: ITEM_VALOR_TIPO, ...item });
}

export function parseItemValorFromAnexo(item: string): SinistroItemValor | null {
  try {
    const parsed = JSON.parse(item) as { _tipo?: string; id?: string; descricao?: string; valor?: number };
    if (parsed?._tipo !== ITEM_VALOR_TIPO) return null;
    const descricao = String(parsed.descricao ?? "").trim();
    const valor = Number(parsed.valor);
    if (!descricao || !Number.isFinite(valor) || valor <= 0) return null;
    return {
      id: String(parsed.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`),
      descricao,
      valor,
    };
  } catch {
    return null;
  }
}

/** Lê itens da coluna itens_valor ou, em bancos sem migration, dos anexos legados. */
export function parseItensValorFromSinistro(sinistro: {
  itens_valor?: unknown;
  anexos?: string[] | null;
}): SinistroItemValor[] {
  const fromColumn = parseItensValor(sinistro.itens_valor);
  if (fromColumn.length > 0) return fromColumn;
  return (sinistro.anexos ?? [])
    .map(parseItemValorFromAnexo)
    .filter((x): x is SinistroItemValor => x != null);
}

export function isItensValorColumnMissing(error: unknown): boolean {
  const e = error as { code?: string; message?: string };
  return e?.code === "PGRST204" && (e.message?.includes("itens_valor") ?? false);
}

export function parseItensValor(raw: unknown): SinistroItemValor[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const descricao = String(o.descricao ?? "").trim();
      const valor = Number(o.valor);
      if (!descricao || !Number.isFinite(valor) || valor <= 0) return null;
      const id = String(o.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
      return { id, descricao, valor };
    })
    .filter((x): x is SinistroItemValor => x != null);
}

export function somarItensValor(itens: SinistroItemValor[]): number {
  return itens.reduce((acc, item) => acc + item.valor, 0);
}

export function getValorTotalSinistro(sinistro: {
  itens_valor?: unknown;
  anexos?: string[] | null;
  valor_estimado?: number | string | null;
  valor_aprovado?: number | string | null;
}): number | undefined {
  const itens = parseItensValorFromSinistro(sinistro);
  if (itens.length > 0) {
    const total = somarItensValor(itens);
    return total > 0 ? total : undefined;
  }
  const bruto = sinistro.valor_aprovado ?? sinistro.valor_estimado;
  if (bruto == null || bruto === "") return undefined;
  const n = parseFloat(String(bruto));
  return !isNaN(n) && n > 0 ? n : undefined;
}

export function formatarMoedaBr(valor: number): string {
  return valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function novoItemValor(descricao: string, valor: number): SinistroItemValor {
  return {
    id: crypto.randomUUID(),
    descricao: descricao.trim(),
    valor,
  };
}

export function buildResumoItensValor(itens: SinistroItemValor[]): string {
  if (itens.length === 0) return "";
  return itens
    .map((i) => `${i.descricao} (R$ ${formatarMoedaBr(i.valor)})`)
    .join("; ");
}
